import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const HeroSection = () => {
  return (
    <section className="relative bg-white overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, var(--color-primary) 1px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}
        />
      </div>

      <div className="relative max-w-wide mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[calc(100vh-6rem)] py-16 lg:py-24">
          {/* Content */}
          <div className="space-y-8 animate-slide-up">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-muted rounded-full">
              <span className="w-2 h-2 bg-accent rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-muted-foreground">
                Now operating in 4 markets
              </span>
            </div>

            {/* Headline */}
            <div className="space-y-6">
              <h1 className="text-hero text-foreground">
                Building the Future of Local Services in Latin America
              </h1>
              <p className="text-body-large max-w-xl">
                A unified marketplace infrastructure for service discovery, booking, trust, commerce, and real-time data.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/investor-relations"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary text-white font-medium rounded-brand-sm hover:bg-primary-light transition-colors duration-200"
              >
                Investor Relations
                <Icon name="ArrowRight" size={18} />
              </Link>
              <Link
                to="/partnerships"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-border text-foreground font-medium rounded-brand-sm hover:bg-muted transition-colors duration-200"
              >
                Partnership Opportunities
              </Link>
            </div>

            {/* Social Proof */}
            <div className="pt-8 border-t border-border">
              <p className="text-sm text-muted-foreground mb-4">Backed by</p>
              <div className="flex items-center gap-8 opacity-60">
                <div className="text-lg font-semibold text-foreground">Y Combinator</div>
                <div className="text-lg font-semibold text-foreground">Stripe</div>
                <div className="text-lg font-semibold text-foreground">Azure</div>
              </div>
            </div>
          </div>

          {/* Visual - Platform Preview */}
          <div className="relative animate-fade-in" style={{ animationDelay: '0.3s' }}>
            <div className="relative">
              {/* Main Dashboard Preview */}
              <div className="bg-white rounded-brand-lg shadow-corporate-lg border border-border overflow-hidden">
                {/* Browser Chrome */}
                <div className="flex items-center gap-2 px-4 py-3 bg-muted border-b border-border">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="bg-white rounded-md px-3 py-1.5 text-xs text-muted-foreground">
                      app.unitedempresaas.com/dashboard
                    </div>
                  </div>
                </div>

                {/* Dashboard Content */}
                <div className="p-6 bg-background-alt">
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-white rounded-brand-sm p-4 border border-border">
                      <div className="text-2xl font-semibold text-foreground">12.4K</div>
                      <div className="text-xs text-muted-foreground">Active Vendors</div>
                    </div>
                    <div className="bg-white rounded-brand-sm p-4 border border-border">
                      <div className="text-2xl font-semibold text-accent">$2.1M</div>
                      <div className="text-xs text-muted-foreground">GMV This Month</div>
                    </div>
                    <div className="bg-white rounded-brand-sm p-4 border border-border">
                      <div className="text-2xl font-semibold text-secondary">98.2%</div>
                      <div className="text-xs text-muted-foreground">Booking Rate</div>
                    </div>
                  </div>

                  {/* Chart Placeholder */}
                  <div className="bg-white rounded-brand-sm p-4 border border-border">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-sm font-medium text-foreground">Booking Volume</div>
                      <div className="text-xs text-accent">+24% vs last month</div>
                    </div>
                    <div className="h-32 flex items-end gap-2">
                      {[40, 65, 45, 80, 55, 90, 70, 95, 60, 85, 75, 100].map((height, i) => (
                        <div
                          key={i}
                          className="flex-1 bg-primary/20 rounded-t"
                          style={{ height: `${height}%` }}
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Card - Vendor App */}
              <div className="absolute -left-8 bottom-16 bg-white rounded-brand shadow-corporate-md border border-border p-4 w-48 animate-slide-up" style={{ animationDelay: '0.5s' }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
                    <Icon name="Store" size={20} className="text-accent" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">Vendor App</div>
                    <div className="text-xs text-muted-foreground">iOS & Android</div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Manage bookings, payments, and customers in one place.
                </div>
              </div>

              {/* Floating Card - Customer App */}
              <div className="absolute -right-4 top-24 bg-white rounded-brand shadow-corporate-md border border-border p-4 w-48 animate-slide-up" style={{ animationDelay: '0.7s' }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-secondary/10 rounded-full flex items-center justify-center">
                    <Icon name="Users" size={20} className="text-secondary" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">Customer App</div>
                    <div className="text-xs text-muted-foreground">iOS & Android</div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Discover and book trusted local services instantly.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
